import { useState, useEffect, useRef, useCallback } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Image, Upload, Trash2, GripVertical, Eye, EyeOff, ChevronUp, ChevronDown, X, Check, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";

const MAX_PHOTOS = 100;

export default function OwnerGalleryPage() {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const fileRef = useRef(null);
  const [menuPhoto, setMenuPhoto] = useState(null);
  const [menuForm, setMenuForm] = useState({ name: "", description: "", price: "", category: "", dietary_tags: [] });
  const [menuSaving, setMenuSaving] = useState(false);
  const DIETARY_TAGS = ["vegetarian", "vegan", "gluten-free", "dairy-free", "nut-free", "spicy", "halal"];

  const fetchPhotos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/owner/gallery`);
      setPhotos(data);
    } catch {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  // ─── Upload ───
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) { toast.error(`Maximum ${MAX_PHOTOS} photos reached`); return; }

    const toUpload = files.slice(0, remaining);
    if (toUpload.length < files.length) toast.info(`Only uploading ${toUpload.length} of ${files.length} (limit: ${MAX_PHOTOS})`);

    setUploading(true);
    let uploaded = 0;
    for (const file of toUpload) {
      if (!file.type.startsWith("image/")) continue;
      const fd = new FormData();
      fd.append("file", file);
      try {
        await axios.post(`${API}/owner/gallery`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded++;
      } catch (err) {
        toast.error(`Failed: ${file.name} - ${err.response?.data?.detail || "error"}`);
      }
    }
    if (uploaded > 0) toast.success(`${uploaded} photo${uploaded > 1 ? "s" : ""} uploaded`);
    fetchPhotos();
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ─── Toggle active ───
  const handleToggleActive = async (photo) => {
    try {
      await axios.put(`${API}/owner/gallery/${photo.photo_id}`, { is_active: !photo.is_active });
      setPhotos(photos.map(p => p.photo_id === photo.photo_id ? { ...p, is_active: !photo.is_active } : p));
      toast.success(photo.is_active ? "Photo disabled" : "Photo enabled");
    } catch { toast.error("Failed to toggle"); }
  };

  // ─── Delete ───
  const handleDelete = async (photoId) => {
    if (!window.confirm("Delete this photo permanently?")) return;
    try {
      await axios.delete(`${API}/owner/gallery/${photoId}`);
      setPhotos(photos.filter(p => p.photo_id !== photoId));
      toast.success("Photo deleted");
    } catch { toast.error("Failed to delete"); }
  };

  // ─── Edit title/description ───
  const startEdit = (photo) => {
    setEditingId(photo.photo_id);
    setEditForm({ title: photo.title || "", description: photo.description || "" });
  };

  const saveEdit = async (photoId) => {
    try {
      await axios.put(`${API}/owner/gallery/${photoId}`, editForm);
      setPhotos(photos.map(p => p.photo_id === photoId ? { ...p, ...editForm } : p));
      setEditingId(null);
      toast.success("Updated");
    } catch { toast.error("Failed to save"); }
  };

  // ─── Drag reorder ───
  const handleDragStart = (idx) => { setDragIdx(idx); };
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const handleDrop = async (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { handleDragEnd(); return; }
    const reordered = [...photos];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setPhotos(reordered);
    handleDragEnd();
    try {
      await axios.put(`${API}/owner/gallery/reorder/batch`, { order: reordered.map(p => p.photo_id) });
      toast.success("Order saved");
    } catch { toast.error("Failed to save order"); fetchPhotos(); }
  };

  // ─── Move up/down ───
  const movePhoto = async (idx, direction) => {
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= photos.length) return;
    const reordered = [...photos];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setPhotos(reordered);
    try {
      await axios.put(`${API}/owner/gallery/reorder/batch`, { order: reordered.map(p => p.photo_id) });
    } catch { fetchPhotos(); }
  };

  const activeCount = photos.filter(p => p.is_active).length;
  const disabledCount = photos.filter(p => !p.is_active).length;

  const handleAddToMenu = async () => {
    if (!menuForm.name) { toast.error("Name is required"); return; }
    setMenuSaving(true);
    try {
      const payload = { name: menuForm.name, description: menuForm.description, category: menuForm.category, dietary_tags: menuForm.dietary_tags };
      if (menuForm.price) payload.price = parseFloat(menuForm.price);
      const res = await axios.post(`${API}/owner/menu/flat`, payload);
      // Upload the photo as the menu item image
      if (menuPhoto && res.data.item_id) {
        const photoData = await fetch(`${API.replace('/api', '')}${menuPhoto.url}`);
        const blob = await photoData.blob();
        const formData = new FormData();
        formData.append("file", blob, menuPhoto.filename || "dish.jpg");
        await axios.post(`${API}/owner/menu/flat/${res.data.item_id}/image`, formData);
      }
      toast.success(`"${menuForm.name}" added to menu`);
      setMenuPhoto(null);
      setMenuForm({ name: "", description: "", price: "", category: "", dietary_tags: [] });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add to menu"); }
    finally { setMenuSaving(false); }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-28 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div data-testid="owner-gallery-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-foreground">{t("owner_gallery:title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {photos.length} / {MAX_PHOTOS} {t("owner_gallery:photos_count")} ({activeCount} {t("owner_gallery:active")}{disabledCount > 0 ? `, ${disabledCount} ${t("owner_gallery:disabled")}` : ""})
          </p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="gallery-upload-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading || photos.length >= MAX_PHOTOS} data-testid="gallery-upload-btn">
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? t("owner_gallery:uploading") : t("owner_gallery:upload")}
          </Button>
        </div>
      </div>

      {photos.length > 0 ? (
        <div className="space-y-3">
          {photos.map((photo, idx) => {
            const isEditing = editingId === photo.photo_id;
            const isDragging = dragIdx === idx;
            const isDragOver = dragOverIdx === idx;
            return (
              <div
                key={photo.photo_id}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragEnd={handleDragEnd}
                onDrop={() => handleDrop(idx)}
                className={`flex items-stretch gap-0 bg-card rounded-xl border overflow-hidden transition-all ${
                  isDragging ? "opacity-40 border-primary" : isDragOver ? "border-primary border-dashed" : "border-border"
                } ${!photo.is_active ? "opacity-60" : ""}`}
                data-testid={`gallery-photo-${photo.photo_id}`}
              >
                {/* Drag handle */}
                <div className="flex items-center px-2 bg-muted/30 cursor-grab active:cursor-grabbing shrink-0" data-testid={`drag-handle-${photo.photo_id}`}>
                  <GripVertical className="h-5 w-5 text-muted-foreground" />
                </div>

                {/* Thumbnail */}
                <div className="w-32 h-24 sm:w-40 sm:h-28 bg-muted relative shrink-0">
                  {photo.url && !photo.url.includes("placeholder") ? (
                    <img src={photo.url} alt={photo.title || photo.filename} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                  {!photo.is_active && (
                    <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
                      <EyeOff className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">#{idx + 1}</span>
                </div>

                {/* Info / Edit */}
                <div className="flex-1 p-3 min-w-0">
                  {isEditing ? (
                    <div className="space-y-2">
                      <Input
                        placeholder="Title"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="text-sm h-8"
                        data-testid={`edit-title-${photo.photo_id}`}
                        autoFocus
                      />
                      <Textarea
                        placeholder="Description"
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="text-sm min-h-[48px] resize-none"
                        rows={2}
                        data-testid={`edit-desc-${photo.photo_id}`}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEdit(photo.photo_id)} data-testid={`save-edit-${photo.photo_id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                          <X className="h-3.5 w-3.5 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="cursor-pointer" onClick={() => startEdit(photo)} data-testid={`photo-info-${photo.photo_id}`}>
                      <p className="font-medium text-sm text-foreground truncate">
                        {photo.title || <span className="text-muted-foreground italic">{t("owner_gallery:click_title")}</span>}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {photo.description || <span className="italic">No description</span>}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">{photo.filename} &middot; {photo.created_at?.split("T")[0]}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col items-center justify-center gap-1 px-2 border-l border-border shrink-0">
                  <button onClick={() => movePhoto(idx, "up")} disabled={idx === 0} className="p-1 hover:bg-muted rounded disabled:opacity-30" data-testid={`move-up-${photo.photo_id}`}>
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button onClick={() => movePhoto(idx, "down")} disabled={idx === photos.length - 1} className="p-1 hover:bg-muted rounded disabled:opacity-30" data-testid={`move-down-${photo.photo_id}`}>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <div className="h-px w-full bg-border my-0.5" />
                  <button
                    onClick={() => handleToggleActive(photo)}
                    className={`p-1 rounded transition-colors ${photo.is_active ? "hover:bg-yellow-50 text-yellow-600" : "hover:bg-green-50 text-green-600"}`}
                    title={photo.is_active ? "Disable" : "Enable"}
                    data-testid={`toggle-active-${photo.photo_id}`}
                  >
                    {photo.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button onClick={() => handleDelete(photo.photo_id)} className="p-1 hover:bg-red-50 text-red-500 rounded" data-testid={`delete-photo-${photo.photo_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => { setMenuPhoto(photo); setMenuForm({ name: photo.title || "", description: photo.description || "", price: "", category: "" }); }} className="p-1 hover:bg-emerald-50 text-emerald-600 rounded" title="Add to menu" data-testid={`add-to-menu-${photo.photo_id}`}>
                    <UtensilsCrossed className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 bg-card rounded-xl border border-border">
          <Image className="h-14 w-14 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t("owner_gallery:no_photos")}</h3>
          <p className="text-muted-foreground mb-4">{t("owner_gallery:no_photos_sub")}</p>
          <Button onClick={() => fileRef.current?.click()} data-testid="gallery-empty-upload-btn">
            <Upload className="h-4 w-4 mr-2" />
            {t("owner_gallery:upload_first")}
          </Button>
        </div>
      )}

      {photos.length > 0 && (
        <p className="text-xs text-muted-foreground mt-4 text-center">
          Drag photos to reorder, or use arrow buttons. Click title/description to edit. Disabled photos won't appear on venue page.
        </p>
      )}

      {/* Add to Menu Dialog */}
      <Dialog open={!!menuPhoto} onOpenChange={(open) => !open && setMenuPhoto(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Menu</DialogTitle>
          </DialogHeader>
          {menuPhoto && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src={`${API.replace('/api', '')}${menuPhoto.url}`} alt="" className="w-16 h-16 rounded-lg object-cover" />
                <p className="text-sm text-muted-foreground">{menuPhoto.filename}</p>
              </div>
              <div><Label>Dish Name *</Label><Input value={menuForm.name} onChange={e => setMenuForm({ ...menuForm, name: e.target.value })} className="mt-1" placeholder="e.g. Grilled Halloumi" data-testid="menu-from-gallery-name" /></div>
              <div><Label>Description</Label><Input value={menuForm.description} onChange={e => setMenuForm({ ...menuForm, description: e.target.value })} className="mt-1" placeholder="Brief description" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Price (EUR)</Label><Input type="number" step="0.01" min="0" value={menuForm.price} onChange={e => setMenuForm({ ...menuForm, price: e.target.value })} className="mt-1" placeholder="0.00" /></div>
                <div><Label>Category</Label><Input value={menuForm.category} onChange={e => setMenuForm({ ...menuForm, category: e.target.value })} className="mt-1" placeholder="e.g. Starters" /></div>
              </div>
              <div>
                <Label>Dietary Tags</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {DIETARY_TAGS.map(tag => (
                    <button key={tag} type="button" onClick={() => setMenuForm({ ...menuForm, dietary_tags: menuForm.dietary_tags.includes(tag) ? menuForm.dietary_tags.filter(t => t !== tag) : [...menuForm.dietary_tags, tag] })}
                      className={`px-2 py-0.5 rounded-full text-xs transition-colors ${menuForm.dietary_tags.includes(tag) ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                    >{tag}</button>
                  ))}
                </div>
              </div>
              <Button onClick={handleAddToMenu} disabled={menuSaving} className="w-full" data-testid="menu-from-gallery-submit">
                {menuSaving ? "Adding..." : "Add to Menu"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
